/**
 * Helper class for determining battle types based on ice coordinates
 */
export default class BattleType {
    /**
     * Determine battle type based on coordinates (uses exact logic from Rails implementation)
     * @param {string} coordinates - "x,y" format (e.g., "0.5,0.3")
     * @param {string} dangerZone - "high", "medium", "low" (not used in current logic)
     * @returns {string} - "net_front", "board", or "open_ice"
     */
    static determineBattleType(coordinates, dangerZone) {
        if (!coordinates) {
            return 'open_ice'; // Default if no coordinates
        }

        const [x, y] = coordinates.split(',').map(Number);
        
        return this.determineBattleTypeFromCoordinates(x, y);
    }
    
    /**
     * Determine battle type from x,y coordinates (EXACT Rails implementation)
     * @param {number} x - X coordinate (0-1)
     * @param {number} y - Y coordinate (0-1)
     * @returns {string} - "net_front", "board", or "open_ice"
     */
    static determineBattleTypeFromCoordinates(x, y) {
        // Check if in high danger area (converted from CSS percentages to decimal)
        // High danger zones: top: 40.5%, right: 5.5%, width: 11%, height: 18% (and left-side version)
        var rightHighDanger = (x >= 0.835 && x <= 0.945 && y >= 0.405 && y <= 0.585); // right side
        var leftHighDanger = (x >= 0.055 && x <= 0.165 && y >= 0.405 && y <= 0.585);  // left side
        
        if (rightHighDanger || leftHighDanger) {
            return 'net_front';
        }
        
        // Check if in board zones following hockey rink perimeter
        // Hockey rink dimensions: ~200ft x 85ft, boards extend ~6-8ft inward
        // Boundary goes halfway between boards and red lines, not all the way to red lines
        var boardZoneWidth = 0.045; // ~4.5% inward from edges for left/right boards
        var yBoardZoneWidth = 0.10; // Extended padding for top/bottom boards (people click away from boards)
        var cornerRadius = 0.15;    // Larger corner areas to match triangular extensions in reference image
        
        // Calculate distance from edges
        var distFromLeft = x;
        var distFromRight = 1 - x;
        var distFromTop = y;
        var distFromBottom = 1 - y;
        
        // Check straight edges (not in corners)
        var inLeftBoard = (distFromLeft <= boardZoneWidth && (y >= cornerRadius && y <= (1 - cornerRadius)));
        var inRightBoard = (distFromRight <= boardZoneWidth && (y >= cornerRadius && y <= (1 - cornerRadius)));
        var inTopBoard = (distFromTop <= yBoardZoneWidth && (x >= cornerRadius && x <= (1 - cornerRadius)));
        var inBottomBoard = (distFromBottom <= yBoardZoneWidth && (x >= cornerRadius && x <= (1 - cornerRadius)));
        
        // Check corners with triangular extensions (diagonal boundaries)
        // The corners extend diagonally inward beyond the rectangular boundary
        var triangleExtension = 0.20; // Additional diagonal extension for corner triangles (increased to 20%)
        
        // Top-left corner: diagonal from (0, triangleExtension) to (triangleExtension, 0)
        var inTopLeftCorner = (x <= cornerRadius && y <= cornerRadius) && 
                             (distFromLeft <= boardZoneWidth || distFromTop <= yBoardZoneWidth ||
                              (x + y <= triangleExtension)); // Diagonal line
        
        // Top-right corner: diagonal from (1-triangleExtension, 0) to (1, triangleExtension)
        var inTopRightCorner = (x >= (1-cornerRadius) && y <= cornerRadius) && 
                              (distFromRight <= boardZoneWidth || distFromTop <= yBoardZoneWidth ||
                               ((1-x) + y <= triangleExtension)); // Diagonal line
        
        // Bottom-left corner: diagonal from (0, 1-triangleExtension) to (triangleExtension, 1)
        var inBottomLeftCorner = (x <= cornerRadius && y >= (1-cornerRadius)) && 
                                (distFromLeft <= boardZoneWidth || distFromBottom <= yBoardZoneWidth ||
                                 (x + (1-y) <= triangleExtension)); // Diagonal line
        
        // Bottom-right corner: diagonal from (1-triangleExtension, 1) to (1, 1-triangleExtension)
        var inBottomRightCorner = (x >= (1-cornerRadius) && y >= (1-cornerRadius)) && 
                                 (distFromRight <= boardZoneWidth || distFromBottom <= yBoardZoneWidth ||
                                  ((1-x) + (1-y) <= triangleExtension)); // Diagonal line
        
        if (inLeftBoard || inRightBoard || inTopBoard || inBottomBoard || 
            inTopLeftCorner || inTopRightCorner || inBottomLeftCorner || inBottomRightCorner) {
            return 'board';
        }
        
        // Default to open ice
        return 'open_ice';
    }
} 